import React, { useEffect, useState } from 'react';
import ProductImageSlider from 'shared/components/likiee/ProductImageSlider';
import {
  LogoMediumIcon,
  LogoMediumOutlinedIcon,
  LogoSmallIcon,
  VoteLeftArrow,
  VoteRightArrow,
} from 'shared/components/Icons';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { getError } from 'utils/appHelpers';
import { createPollVote, getPollVotesResult, getUserVote, togglePollState } from 'app/routes/Dashboard/actions/polls';
import { API, graphqlOperation } from 'aws-amplify';
import { addLikieeFrom } from 'graphql/likiees/mutations';
import { useHistory } from 'react-router-dom';
import { likieeByURL } from 'graphql/likiees/queries';
import { onAddProduct } from 'app/routes/Dashboard/actions/products';
import { useDispatch, useSelector } from 'react-redux';
import { TidyURL } from 'tidy-url';
import { verifyEmailModal } from 'shared/utils/productHelpers';
import { isEmailVerified } from 'app/actions/authActions';

const PollBlock = ({ poll, hideTitle, className, showAddLikiee, isPollView }) => {
  const account = useSelector(state => state.account);
  const dispatch = useDispatch();
  const [likieeExist, setLikieeExist] = useState({ first: false, second: false });
  const history = useHistory();
  const alert = useAlert();
  const { t } = useTranslation();

  const sliderOptions = { dots: false };
  const votesResult = getPollVotesResult(poll?.votes);
  const isUserVoted = getUserVote(poll?.votes);

  const showPercents = poll.isOwner || isUserVoted;
  const firstImages = poll.first?.images ? [poll.firstImage, ...poll.first.images] : [poll.firstImage];
  const secondImages = poll.second?.images ? [poll.secondImage, ...poll.second.images] : [poll.secondImage];
  const isVotedFirst = isUserVoted === 'FIRST';
  const isVotedSecond = isUserVoted === 'SECOND';

  const onTogglePoll = async () => {
    try {
      await togglePollState(poll.id, poll.isActive ? 0 : 1);
    } catch (err) {
      alert.error(getError(err));
    }
  };

  const onVotePoll = async vote => {
    if (!isEmailVerified(account)) {
      verifyEmailModal(alert, t);
      return;
    }

    try {
      await createPollVote(poll.id, vote);
    } catch (err) {
      alert.error(getError(err));
    }
  };

  const onSaveLikiee = async item => {
    try {
      const input = { id: item.id };
      const addedLikee = await API.graphql(graphqlOperation(addLikieeFrom, input));
      setLikieeExist({ ...setLikieeExist, [item.type]: true });
      dispatch(onAddProduct(addedLikee.data.addLikieeFrom));
    } catch (err) {
      alert.error(getError(err));
    }
  };

  const checkLikieeByUrl = async poll => {
    try {
      let likieeFirst, likieeSecond;
      if (poll.first?.url) {
        const firstInput = { url: TidyURL.clean(poll.first?.url).url };
        likieeFirst = await API.graphql(graphqlOperation(likieeByURL, firstInput));
      }
      if (poll.second?.url) {
        const secondInput = { url: TidyURL.clean(poll.second?.url).url };
        likieeSecond = await API.graphql(graphqlOperation(likieeByURL, secondInput));
      }
      setLikieeExist({
        first: !!likieeFirst?.data?.likieeByURL.items?.length,
        second: !!likieeSecond?.data?.likieeByURL.items?.length,
      });
    } catch (err) {
      alert.error(getError(err));
    }
  };

  const openPollPage = () => {
    history.push(`/poll/${poll.id}`);
  };

  useEffect(() => {
    showAddLikiee && checkLikieeByUrl(poll);
  }, [poll]);

  const likieeIds = [{ id: poll.firstLikieeId, type: 'first' }, { id: poll.secondLikieeId, type: 'second' }];

  return (
    <div className={`poll-block position-relative ${className || ''}`}>
      {poll.isOwner && (
        <div className='toggle-poll text-center'>
          <button className='btn text-white btn-sm' onClick={onTogglePoll}>
            {poll.isActive ? t('btn_deactivate') : t('btn_activate')}
          </button>
        </div>
      )}
      <div className='poll-image-block'>
        <div className='d-flex rounded-10 border overflow-hidden'>
          <div
            className='col-6 border-right p-0'
            onClick={isPollView ? null : openPollPage}
            role='button'
            tabIndex={-1}
          >
            <ProductImageSlider options={sliderOptions} images={firstImages} />
          </div>
          <div className='col-6 border-left p-0' onClick={isPollView ? null : openPollPage} role='button' tabIndex={-1}>
            <ProductImageSlider options={sliderOptions} images={secondImages} />
          </div>
        </div>
        <div
          className={`poll-percent d-flex justify-content-center align-items-center ${
            showPercents ? '' : 'show-vote-btns'
          }`}
        >
          <p
            className={`d-flex align-items-center flex-grow-1 mb-0 fw-500 d-md-none text-center ${
              (isUserVoted && !isVotedFirst) || poll.isOwner ? 'op-0' : ''
            }`}
          >
            <VoteLeftArrow className='mr-1' />
            <span>{isUserVoted ? t('text_voted') : t('text_vote')}</span>
          </p>
          <div className='d-flex justify-content-between'>
            <button
              className={`btn ${isUserVoted === 'FIRST' ? 'is-selected' : ''}`}
              disabled={showPercents}
              onClick={() => onVotePoll('FIRST')}
            >
              {showPercents ? `${votesResult.first}%` : <LogoMediumIcon />}
            </button>
            {!showPercents && <span>{t('text_or')}</span>}
            <button
              className={`btn ${isUserVoted === 'SECOND' ? 'is-selected' : ''}`}
              disabled={showPercents}
              onClick={() => onVotePoll('SECOND')}
            >
              {showPercents ? `${votesResult.second}%` : <LogoMediumIcon />}
            </button>
          </div>
          <p
            className={`d-flex align-items-center flex-grow-1 mb-0 fw-500 d-md-none text-center justify-content-end ${
              (isUserVoted && !isVotedSecond) || poll.isOwner ? 'op-0' : ''
            }`}
          >
            <span>{isUserVoted ? t('text_voted') : t('text_vote')}</span>
            <VoteRightArrow className='ml-1' />
          </p>
        </div>
      </div>
      {showAddLikiee && (
        <div className='row mt-0 mt-md-10 add-item-buttons'>
          {likieeIds.map((item, i) => {
            const isExists = !!likieeExist[item.type];
            if (!poll[item.type]?.url) return null;
            return (
              <div className='col-6 d-flex align-items-center' key={i}>
                <button
                  className='btn p-0 d-flex align-items-center op-10'
                  disabled={isExists}
                  onClick={() => onSaveLikiee(item)}
                >
                  {!isExists ? (
                    <LogoMediumOutlinedIcon width={30} height={30} />
                  ) : (
                    <LogoSmallIcon width={30} height={30} />
                  )}
                  <p className='mb-0 ml-5 text-gray-dark fw-300'>
                    {t(isExists ? 'poll_likiee_added' : 'poll_likiee_add')}
                  </p>
                </button>
              </div>
            );
          })}
        </div>
      )}
      <h2 className={`mt-md-10 mt-7 pt-7 pt-md-0 mb-0 text-center text-md-left ${hideTitle ? 'op-0' : ''}`}>
        <Link className='text-body' to={`/poll/${poll.id}`}>
          {poll.title}
        </Link>
      </h2>
    </div>
  );
};

export default PollBlock;
